import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '@/store/tenantStore';
import { getLogoImageUrl } from '@/services/tenant/tenantImages';
import { useAuthStore } from '@/store/authStore';
import { ROUTES } from '@/app/router/routes';
import { signOutUser } from '@/services/auth/authService';
import { useLocalizationStore } from '@/store/localizationStore';
import { changeLanguage } from '@/i18n';
import { auth } from '@/config/firebase';
import { prefetchDynamicForms, type CandidateDetails } from '@/services/api/authApi';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useAppStateStore } from '@/store/appStateStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { DynamicHeader, collectHeaderNav, useDynamicConfig } from './DynamicHeaderFooter';
import { MobileNavMenu } from './MobileNavMenu';

/**
 * AppHeader — reference UI sticky header.
 * Mirrors Flutter's WebPageHeader with tenant content.
 *
 * Layout: [Logo] [spacer] [Tenant links] [Saved Jobs] [Language] [Login | User avatar].
 * Every header item uses the tenant's header title style (themeSettings.header.title) so it
 * matches the tenant links.
 *
 * Redesign deviation from Flutter's WebPageHeader: no Home button (the logo goes home).
 * Like Flutter, the login, sign-up and MFA pages have no header (features/auth AuthLayout).
 *
 * On mobile it stays one row like WebPageHeader's isMobile branch: Saved Jobs and Login
 * become icons (_buildSavedJobIconWidget), and the avatar drops the first name. The tenant
 * header links move into a ☰ menu panel (MobileNavMenu): [Logo] … [Saved] [Avatar] [☰]
 *
 * Every navigation logs screen_view first, as Flutter's NavigationService.navigateTo(route,
 * analyticsClass) does — with Flutter's route and analyticsClass.
 */
interface AppHeaderProps {
  /**
   * Flutter WebPageHeader(isDuplicateAccount: true) on the duplicate-account page: Profile,
   * My Jobs and Saved Jobs do nothing (web_page_header.dart _buildSavedJobWidget,
   * _buildSavedJobIconWidget and the PROFILE / MYJOBS menu branches).
   */
  isDuplicateAccount?: boolean;
}

/** CandidateDetails fields are loosely typed (index signature) — read them as text */
function text(value: unknown): string {
  return typeof value === 'string' ? value : value == null ? '' : String(value);
}

export function AppHeader({ isDuplicateAccount = false }: AppHeaderProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { content, headerFooterConfig, theme } = useTenantStore();
  const { user, isAuthorized } = useAuthStore();
  const savedJobsCount = useAppStateStore((s) => s.savedJobsCount);
  const candidateDetails = useAppStateStore((s) => s.candidateDetails);
  const candidateFirstName = text(candidateDetails?.firstName);

  const general = content?.contentSettings?.general;
  // Flutter WebFontConstants.headerTitleTextStyleData (themeSettings.header.title) styles Home,
  // Saved Jobs, the language button, the user's name and Login. Shared with the items below
  // through CSS variables on <header>; without it they keep the reference UI muted 13px.
  const headerTitle = theme?.themeSettings?.header?.title;
  const headerItemVars = {
    '--header-item-color': headerTitle?.fontColor || 'var(--ink-muted)',
    '--header-item-size': !isMobile && headerTitle?.fontSize ? `${headerTitle.fontSize}px` : '13px',
    '--header-item-weight': Number(headerTitle?.fontWeight) || headerTitle?.fontWeight || 400,
  } as CSSProperties;
  // Flutter WebPageHeader renders DynamicHeader from headerFooterConfig next to the logo
  const headerModel = useDynamicConfig(headerFooterConfig, 'header');

  const logoUrl = getLogoImageUrl(content);
  // Flutter WebPageHeader bounds the desktop logo with ConstrainedBox(maxHeight:
  // logoProperties.height.h, maxWidth: logoProperties.width.w), i.e. % of the viewport,
  // around a BoxFit.contain image. Without tenant sizes keep the reference UI's 104×32 slot.
  const logoSize = general?.logoProperties;
  const desktopLogoStyle: CSSProperties = logoSize?.height || logoSize?.width
    ? {
        width: 'auto', height: 'auto', objectFit: 'contain',
        maxHeight: logoSize.height ? `${logoSize.height}vh` : 32,
        maxWidth: logoSize.width ? `${logoSize.width}vw` : 104,
      }
    : { width: 104, height: 32, objectFit: 'contain' };
  // Flutter AppConfig.appTitle: contentSettings.general.name ?? 'Career Portal'
  const brandName = general?.name ?? 'Career Portal';
  const navGroups = isMobile ? collectHeaderNav(headerModel) : [];
  const isSavedJobsPage = location.pathname === ROUTES.VIEW_JOBS_SAVED;

  // Logo: navigateTo(homeRoute, isClearStack: true, analyticsClass: 'click_on_company_logo')
  const goHome = () => {
    void analyticsService.logScreenView({ screenName: ROUTES.HOME, screenClass: 'click_on_company_logo' });
    navigate(ROUTES.HOME);
  };

  // Flutter WebPageHeader saved-jobs tap: profile › My Jobs › Saved, sign-up while unverified,
  // login (returning to Saved) when signed out
  const openSavedJobs = () => {
    if (isDuplicateAccount) return;
    if (user) {
      if (user.emailVerified) {
        // navigateTo(profile, analyticsClass: 'click_on_saved_jobs', ProfileInput(1, 3))
        void analyticsService.logScreenView({ screenName: ROUTES.PROFILE, screenClass: 'click_on_saved_jobs' });
        navigate(ROUTES.VIEW_JOBS_SAVED);
      } else {
        void analyticsService.logScreenView({ screenName: ROUTES.SIGN_UP });
        navigate(ROUTES.SIGN_UP);
      }
    } else {
      void analyticsService.logScreenView({ screenName: ROUTES.LOGIN, screenClass: 'click_on_saved_jobs' });
      navigate(ROUTES.LOGIN, { state: { previousPage: ROUTES.SAVED_JOBS } });
    }
  };

  const openLogin = () => {
    void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
    navigate(ROUTES.LOGIN);
  };

  return (
    <header style={{
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '2px' : '14px',
      ...(isMobile ? { height: 56, padding: '0 8px 0 16px' } : { padding: '10px var(--page-gutter)' }),
      background: 'var(--header-bg, #fff)',
      borderBottom: '1px solid var(--border-soft)',
      position: 'sticky',
      top: 0,
      zIndex: 30,
      ...headerItemVars,
    }}>
      {/* Logo — a link home, so it is reachable and announced like one (the image alt names it) */}
      <a
        href={ROUTES.HOME}
        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flexShrink: 0 }}
        onClick={(e) => {
          e.preventDefault();
          goHome();
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={brandName}
            style={isMobile
              ? { height: 36, width: 'auto', maxWidth: 132, objectFit: 'contain' }
              : desktopLogoStyle}
          />
        ) : (
          <div style={{
            width: isMobile ? 88 : 104, height: 32,
            border: '1px dashed #D1D5DB',
            borderRadius: '6px',
            display: 'grid', placeItems: 'center',
            fontSize: '10px', fontWeight: 600,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: 'rgba(107,114,128,0.75)',
          }}>
            Client logo
          </div>
        )}
      </a>

      <div style={{ flex: 1, minWidth: isMobile ? 8 : 12 }} />

      {/* Tenant header links (headerFooterConfig) sit right before Saved Jobs; on mobile they
          live in the ☰ menu instead. The header never wraps: when space runs out the links wrap
          inside their own box. */}
      {!isMobile && headerModel && (
        <div style={{ flex: '0 1 auto', minWidth: 0 }}>
          <DynamicHeader model={headerModel} />
        </div>
      )}

      {/* Saved Jobs */}
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <NavButton
          label={t('Saved Jobs')}
          icon={isMobile ? <BookmarkIcon filled={isSavedJobsPage} /> : undefined}
          leadingIcon={isMobile ? undefined : <BookmarkIcon filled={isSavedJobsPage} size={18} />}
          active={isSavedJobsPage}
          onClick={openSavedJobs}
          badge={user && savedJobsCount > 0 ? savedJobsCount : undefined}
        />
      </div>

      {/* Language switcher (Flutter WebPageHeader PopupMenuButton over supportedLanguage) */}
      <LanguageMenu />

      {!user && (
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isMobile ? (
            <NavButton label={t('Login')} icon={<PersonIcon />} active={false} onClick={openLogin} />
          ) : (
            <LoginButton label={t('Login')} onClick={openLogin} />
          )}
        </div>
      )}

      {/* User avatar + menu (Flutter PopupMenuButton: user card / Profile / My Jobs / Sign Out) */}
      {user && (
        <UserMenu
          name={isAuthorized && !isMobile ? candidateFirstName : ''}
          candidate={candidateDetails}
          disabled={!user.emailVerified}
          compact={isMobile}
          isDuplicateAccount={isDuplicateAccount}
        />
      )}

      {navGroups.length > 0 && (
        <MobileNavMenu groups={navGroups} logoUrl={logoUrl} brandName={brandName} />
      )}
    </header>
  );
}

/**
 * Text pill on desktop; with `icon`, an icon-only button labelled for screen readers (mobile)
 * that shows its active state through the icon colour rather than a filled pill.
 */
function NavButton({ label, icon, leadingIcon, active, onClick, badge }: {
  label: string;
  icon?: ReactNode;
  /** Icon shown before the text label on desktop */
  leadingIcon?: ReactNode;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={icon ? label : undefined}
      title={icon ? label : undefined}
      style={{
        position: 'relative',
        fontSize: 'var(--header-item-size)',
        ...(icon
          ? { width: 40, height: 40, padding: 0, display: 'grid', placeItems: 'center' }
          : { padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 8 }),
        borderRadius: 'var(--radius-pill)', border: 'none',
        background: active && !icon ? 'var(--brand-soft)' : 'transparent',
        color: active ? 'var(--brand)' : icon ? 'var(--ink-primary)' : 'var(--header-item-color)',
        fontWeight: active ? 600 : 'var(--header-item-weight)' as CSSProperties['fontWeight'],
        cursor: 'pointer',
        transition: 'all var(--dur-fast) var(--ease-out)',
      }}
    >
      {icon ?? (<>{leadingIcon}{label}</>)}
      {badge !== undefined && (
        <span style={{
          position: 'absolute', textAlign: 'center',
          background: 'var(--brand)', color: '#fff', fontSize: 10,
          ...(icon
            ? {
                top: 4, right: 3, minWidth: 16, height: 16, padding: '0 4px',
                borderRadius: 8, lineHeight: '16px', fontWeight: 600,
                // ring in the header colour keeps the badge crisp over the icon
                boxShadow: '0 0 0 2px var(--header-bg, #fff)',
              }
            : { top: -4, right: -4, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, lineHeight: '18px' }),
        }}>
          {badge}
        </span>
      )}
    </button>
  );
}

/** Primary call-to-action for signed-out visitors, in the brand colour */
function LoginButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 16px', border: 'none', cursor: 'pointer',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--brand)', color: '#fff',
        fontSize: 'var(--header-item-size)', fontWeight: 600,
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--brand-dark)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--brand)'; }}
    >
      <PersonIcon size={16} />
      {label}
    </button>
  );
}

/**
 * `compact` (mobile): avatar only, no divider, larger menu rows.
 *
 * The avatar button shows a person icon, as Flutter's CircleAvatar(Icons.person) does
 * (web_page_header.dart). The menu opens with Flutter's disabled user card (getDrpDwnList):
 * the first-name initial, full name, "Candidate ID: …", then current role, email and phone,
 * followed by Profile, My Jobs and Sign Out.
 */
function UserMenu({ name, candidate, disabled, compact = false, isDuplicateAccount }: {
  name: string;
  candidate: CandidateDetails | null;
  disabled: boolean;
  compact?: boolean;
  isDuplicateAccount: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const firstName = text(candidate?.firstName);
  const initial = firstName ? firstName[0].toUpperCase() : '';

  // PROFILE / MYJOBS: navigateTo(profile, …) with no analyticsClass; both return early on the
  // duplicate-account page
  const openProfile = (path: string) => {
    setOpen(false);
    if (isDuplicateAccount) return;
    void analyticsService.logScreenView({ screenName: ROUTES.PROFILE });
    navigate(path);
  };

  const handleSignOut = async () => {
    setOpen(false);
    setSigningOut(true);
    try {
      await signOutUser();
    } finally {
      // Flutter navigates home then reloads the window to reset all state
      void analyticsService.logScreenView({ screenName: ROUTES.HOME });
      window.location.assign(ROUTES.HOME);
    }
  };

  const items: { key: string; label: string; onClick: () => void }[] = [
    { key: 'PROFILE', label: t('Profile'), onClick: () => openProfile(ROUTES.PROFILE) },
    { key: 'MYJOBS', label: t('My Jobs'), onClick: () => openProfile(ROUTES.VIEW_JOBS) },
    { key: 'LOGOUT', label: t('Sign Out'), onClick: () => void handleSignOut() },
  ];

  return (
    <div ref={menuRef} style={{ position: 'relative', borderLeft: compact ? undefined : '1px solid var(--border-soft)' }}>
      <button
        type="button"
        disabled={disabled || signingOut}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        // Always named; with the first name shown, the name stays part of it
        aria-label={name ? `${t('Profile')}: ${name}` : t('Profile')}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: compact ? 4 : name ? '4px 6px 4px 10px' : '4px 4px 4px 8px', borderRadius: 'var(--radius-pill)',
          border: 'none', background: 'transparent',
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {name && (
          <span style={{
            fontSize: 'var(--header-item-size)',
            color: 'var(--header-item-color)',
            fontWeight: 'var(--header-item-weight)' as CSSProperties['fontWeight'],
          }}>
            {name}
          </span>
        )}
        <div style={{
          width: compact ? 32 : 30, height: compact ? 32 : 30, borderRadius: 'var(--radius-pill)',
          background: 'linear-gradient(135deg, var(--brand), var(--brand-light))',
          color: '#fff', display: 'grid', placeItems: 'center',
          fontSize: '12px', fontWeight: 700,
        }}>
          {signingOut ? '…' : <PersonIcon size={16} />}
        </div>
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            width: 'min(280px, calc(100vw - 80px))', padding: '6px 0',
            background: 'var(--bg-white)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-elev)',
            zIndex: 40,
          }}
        >
          <UserInfoCard candidate={candidate} initial={initial} />
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onClick={item.onClick}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: compact ? '12px 16px' : '8px 14px', border: 'none', background: 'transparent',
                fontSize: compact ? '15px' : '13px', color: 'var(--ink-primary)', cursor: 'pointer',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ellipsis: CSSProperties = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

/**
 * Flutter getDrpDwnList's first PopupMenuItem (enabled: false, SelectionArea): selectable text,
 * not a menu action. Rows without a value are left out, as in Flutter.
 */
function UserInfoCard({ candidate, initial }: { candidate: CandidateDetails | null; initial: string }) {
  const { t } = useTranslation();
  const fullName = `${text(candidate?.firstName)} ${text(candidate?.lastName)}`.trim();
  const displayId = text(candidate?.displayId);
  const currentRole = text(candidate?.currentRole);
  const email = text(candidate?.primaryEmailId);
  const phone = text(candidate?.primaryContactNumber);
  const phoneCode = text(candidate?.primaryContactNumberCountryCode);
  const details = [
    currentRole,
    email,
    phone ? `+${phoneCode} ${phone}` : '',
  ].filter(Boolean);

  return (
    <div
      role="presentation"
      style={{ cursor: 'text', userSelect: 'text', borderBottom: '1px solid var(--border)', marginBottom: 6 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 10px' }}>
        <div
          aria-hidden="true"
          style={{
            width: 32, height: 32, flexShrink: 0, borderRadius: 'var(--radius-pill)',
            background: 'var(--surface-bg)', border: '1px solid var(--border)',
            color: 'var(--ink-muted)', display: 'grid', placeItems: 'center',
            fontSize: 14, fontWeight: 700,
          }}
        >
          {initial}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ ...ellipsis, fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>
            {fullName}
          </div>
          {displayId && (
            <div style={{ ...ellipsis, fontSize: 11, color: 'var(--ink-muted)' }}>
              {`${t('Candidate ID')}: ${displayId}`}
            </div>
          )}
        </div>
      </div>
      {details.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border-soft)', padding: '8px 14px 10px' }}>
          {details.map((detail) => (
            <div key={detail} style={{ ...ellipsis, padding: '3px 0', fontSize: 12, color: 'var(--ink-primary)' }}>
              {detail}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Flutter shows the upper-cased language code (getAppLanguage().toUpperCase()) at every width. */
function LanguageMenu() {
  const { t } = useTranslation();
  const supportedLanguages = useLocalizationStore((s) => s.supportedLanguages);
  const currentLanguage = useLocalizationStore((s) => s.currentLanguage);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (supportedLanguages.length === 0) return null;

  // Mirrors Flutter LocalizationProvider.changeLocale
  const select = async (code: string) => {
    setOpen(false);
    if (code === currentLanguage) return;
    const fromLanguage = currentLanguage;
    // changeLanguage persists appLanguage/oldLanguage; only sync the store value here
    await changeLanguage(code);
    useLocalizationStore.setState({ currentLanguage: code });
    if (auth.currentUser) {
      // Dynamic form schemas are localized server-side (language header)
      void prefetchDynamicForms();
    }
    void analyticsService.logLanguageChange({ fromLanguage, toLanguage: code });
  };

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${t('Change language')} (${currentLanguage.toUpperCase()})`}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          fontSize: 'var(--header-item-size)', padding: '6px 10px',
          borderRadius: 'var(--radius-pill)', border: 'none',
          background: 'transparent', color: 'var(--header-item-color)', cursor: 'pointer',
          fontWeight: 'var(--header-item-weight)' as CSSProperties['fontWeight'],
        }}
      >
        <GlobeIcon size={18} />
        {currentLanguage.toUpperCase()}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            minWidth: 140, padding: '6px 0',
            background: 'var(--bg-white)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-elev)',
            zIndex: 40,
          }}
        >
          {supportedLanguages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="menuitemradio"
              aria-checked={lang.code === currentLanguage}
              onClick={() => lang.code && void select(lang.code)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 14px', border: 'none',
                background: lang.code === currentLanguage ? 'var(--brand-soft)' : 'transparent',
                color: lang.code === currentLanguage ? 'var(--brand)' : 'var(--ink-primary)',
                fontSize: '13px', cursor: 'pointer',
              }}
            >
              {lang.name ?? lang.code}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ICON_PROPS = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function BookmarkIcon({ filled = false, size }: { filled?: boolean; size?: number }) {
  return (
    <svg {...ICON_PROPS} {...(size ? { width: size, height: size } : {})} fill={filled ? 'currentColor' : 'none'}>
      <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16Z" />
    </svg>
  );
}

function GlobeIcon({ size }: { size?: number }) {
  return (
    <svg {...ICON_PROPS} {...(size ? { width: size, height: size } : {})}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.5 2.6 3.8 5.6 3.8 9S14.5 18.4 12 21c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3Z" />
    </svg>
  );
}

function PersonIcon({ size }: { size?: number }) {
  return (
    <svg {...ICON_PROPS} {...(size ? { width: size, height: size } : {})}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c.9-3.700 4-5.5 7.5-5.5s6.6 1.8 7.5 5.5" />
    </svg>
  );
}
